import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, IconButton, Modal, Typography, Menu, MenuItem } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Label } from 'recharts';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from '../../../../assets/icons/Expand.svg';
import EditIcon from '../../../../assets/icons/edit.svg';
import CollapseIcon from '../../../../assets/icons/CollapseIcon.svg';
import RestartAltIcon from '../../../../assets/icons/Reset.svg';
import CommonAnalysisTable from './CommonAnalysisTable';
import EditDataModal from './EditDataModal';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';
import ArrowLeftIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';
import ArrowRightIcon from 'packages/novous_web/src/assets/icons/next.svg';

interface ChartPieData {
  title?: string;
  outerRingData?: {
    alertNameWithValueList: Array<{
      alertName: string;
      alertValue: number;
      vehicleId?: string;
      date?: string;
      [key: string]: any;
    }>;
    coloursName?: string[];
    warningTypes?: string[];
  };
  innerRingData?: {
    alertNameWithValueList: Array<{
      alertName: string;
      alertValue: number;
      [key: string]: any;
    }>;
    coloursName?: string[];
    warningTypes?: string[];
  };
  containerClassName?: string;
  centerLabel?: string;
  totalValue?: string;
  infoIcon?: React.ReactNode;
  selectionBox?: boolean;
  getDataForSelection?: (selectedType: string | null) => any;
  getTitleForSelection?: (selectedType: string | null) => string;
  tableTitle?: string;
  tableHeaderText?: string;
  tableColumns?: Array<{ key: string; label: string; align?: 'left' | 'center' | 'right' }>;
  tableData?: any[];
  onSelectionChange?: (selectedType: string | null) => void;
  onTableIconClick?: (selectedAlert: string | null) => void;
  paginationInfo?: any; // For table pagination
  onPaginationChange?: (cursor: any) => void; // For table pagination

  // Chart pagination props (NEW - similar to BarChartCommon)
  paginatedData?: any;
  onChartPaginationChange?: (cursor: any) => void;
  chartPaginationInfo?: {
    currentPage?: number;
    pageSize?: number;
    totalRecords?: number;
    hasMorePages?: boolean;
    nextCursor?: any;
    previousCursor?: any;
  };

  onCardDataUpdate?: (chartId: number, startDate: string, endDate: string) => void;
  chartId?: number;
  showSeparateLegend?: boolean;
}

// Function to generate shades of a base color (darker to lighter)
const generateColorShades = (baseColor: string, count: number): string[] => {
  if (count <= 0) return [baseColor];

  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const shades = [];

  // Create shades from DARKEST to LIGHTEST
  for (let i = 0; i < count; i++) {
    // Factor from 0.3 (darkest) to 1.0 (lightest)
    const factor = 0.3 + (i * 0.7) / (count - 1 || 1);
    const newR = Math.min(255, Math.floor(r * factor));
    const newG = Math.min(255, Math.floor(g * factor));
    const newB = Math.min(255, Math.floor(b * factor));

    shades.push(
      `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB
        .toString(16)
        .padStart(2, '0')}`,
    );
  }

  return shades;
};

const CommonDoublePieChart: React.FC<ChartPieData> = ({
  title: baseTitle,
  outerRingData,
  innerRingData,
  containerClassName,
  centerLabel,
  totalValue,
  infoIcon,
  selectionBox = true,
  getDataForSelection,
  getTitleForSelection,
  tableTitle = 'Analysis Table',
  tableHeaderText = 'Detailed information',
  tableColumns = [],
  tableData = [],
  onSelectionChange,
  onTableIconClick,
  paginationInfo,
  onPaginationChange,
  onCardDataUpdate,
  chartId,
  showSeparateLegend = false,
  // NEW PAGINATION PROPS
  paginatedData,
  onChartPaginationChange,
  chartPaginationInfo,
}) => {
  const { showToast } = useToast();
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [legendView, setLegendView] = useState<'inner' | 'outer' | 'both'>('both');
  const [vehicleColorMap, setVehicleColorMap] = useState<{ [key: string]: string }>({});
  const [vehicleShadeMap, setVehicleShadeMap] = useState<{ [key: string]: string[] }>({});
  const [sortedOuterData, setSortedOuterData] = useState<any[]>([]);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);

  const [cursorHistory, setCursorHistory] = useState<any[]>([]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState<number>(-1);
  const [pageHistory, setPageHistory] = useState<{ cursor: any; pageNum: number }[]>([]);
  const [sentNextCursors, setSentNextCursors] = useState<any[]>([]);
  const [currentPageState, setCurrentPageState] = useState<number | null>(null);

  // Determine if we're using separate data or the old single data format
  const hasSeparateData = outerRingData && innerRingData;

  // For backward compatibility
  const data = getDataForSelection ? getDataForSelection(selectedAlert) : null;

  const title = getTitleForSelection ? getTitleForSelection(selectedAlert) : baseTitle;

  // Assign colors to vehicles and generate shades
  useEffect(() => {
    if (!hasSeparateData || !innerRingData || !outerRingData) return;

    const baseColors = [
      '#C83333', // Red
      '#F2994A', // Orange
      '#27AE60', // Green
      '#2D9CDB', // Blue
      '#BB6BD9', // Purple
      '#F2C94C', // Yellow
      '#5547DD', // Indigo
      '#E74C3C', // Dark Red
      '#1ABC9C', // Teal
      '#9B59B6', // Violet
      '#3498DB', // Light Blue
      '#2ECC71', // Emerald
      '#E67E22', // Carrot
      '#95A5A6', // Grey
    ];

    const newVehicleColorMap: { [key: string]: string } = {};
    const newVehicleShadeMap: { [key: string]: string[] } = {};

    // Step 1: Group outer ring data by vehicle
    const vehicleGroups: { [key: string]: any[] } = {};

    outerRingData.alertNameWithValueList.forEach((entry, index) => {
      let vehicleId = '';

      if (entry.vehicleId) {
        vehicleId = entry.vehicleId;
      } else if (entry.alertName.includes('(')) {
        // Extract vehicle ID from format "HR55AX6879 (2025-12-02)"
        vehicleId = entry.alertName.split('(')[0].trim();
      } else {
        vehicleId = `Vehicle-${index}`;
      }

      if (!vehicleGroups[vehicleId]) {
        vehicleGroups[vehicleId] = [];
      }

      vehicleGroups[vehicleId].push({
        ...entry,
        originalIndex: index,
        vehicleId: vehicleId,
      });
    });

    // Step 2: Sort each vehicle's entries by date (if available)
    Object.keys(vehicleGroups).forEach((vehicleId) => {
      vehicleGroups[vehicleId].sort((a, b) => {
        // Try to extract and compare dates
        const dateA =
          a.date || (a.alertName.includes('(') ? a.alertName.split('(')[1]?.replace(')', '') : '');
        const dateB =
          b.date || (b.alertName.includes('(') ? b.alertName.split('(')[1]?.replace(')', '') : '');

        if (dateA && dateB) {
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        }
        return a.originalIndex - b.originalIndex;
      });
    });

    // Step 3: Create sorted array with vehicles grouped together
    const sortedData: any[] = [];
    Object.keys(vehicleGroups).forEach((vehicleId) => {
      sortedData.push(...vehicleGroups[vehicleId]);
    });

    setSortedOuterData(sortedData);

    // Step 4: Assign base colors to vehicles
    innerRingData.alertNameWithValueList.forEach((vehicle, index) => {
      const vehicleId = vehicle.alertName;
      const baseColor = baseColors[index % baseColors.length];
      newVehicleColorMap[vehicleId] = baseColor;
    });

    // Step 5: Generate shades for each vehicle based on number of entries
    Object.keys(vehicleGroups).forEach((vehicleId) => {
      const count = vehicleGroups[vehicleId].length;
      const baseColor =
        newVehicleColorMap[vehicleId] ||
        baseColors[Object.keys(newVehicleColorMap).length % baseColors.length];
      const shades = generateColorShades(baseColor, count);
      newVehicleShadeMap[vehicleId] = shades;
    });

    setVehicleColorMap(newVehicleColorMap);
    setVehicleShadeMap(newVehicleShadeMap);
  }, [outerRingData, innerRingData, hasSeparateData]);

  // Prepare data for separate rings with grouped vehicles
  const prepareSeparatePieData = useCallback(() => {
    if (!hasSeparateData || !innerRingData || sortedOuterData.length === 0) {
      return { innerPieData: [], outerPieData: [] };
    }

    // Prepare inner ring data (vehicle totals)
    const innerPieData = innerRingData.alertNameWithValueList
      .map((vehicle, index) => ({
        warningName: vehicle.alertName,
        colorCode:
          vehicleColorMap[vehicle.alertName] || `#${Math.floor(Math.random() * 16777215).toString(16)}`,
        value: vehicle.alertValue,
        percentage: vehicle.percentage,
        type: 'inner',
        originalData: vehicle,
        vehicleId: vehicle.alertName,
      }))
      .filter((item) => item.value > 0);

    // Prepare outer ring data with grouped vehicles
    const outerPieData: any[] = [];
    const vehicleShadeIndexMap: { [key: string]: number } = {};

    // Use the sorted outer data (vehicles already grouped)
    sortedOuterData.forEach((entry, index) => {
      const vehicleId = entry.vehicleId;

      if (!vehicleId) return;

      // Initialize shade index for this vehicle if not exists
      if (vehicleShadeIndexMap[vehicleId] === undefined) {
        vehicleShadeIndexMap[vehicleId] = 0;
      }

      // Get the shade for this entry
      const shades = vehicleShadeMap[vehicleId] || [vehicleColorMap[vehicleId] || '#cccccc'];
      const shadeIndex = vehicleShadeIndexMap[vehicleId];
      const color = shades[shadeIndex % shades.length];

      // Increment shade index for next entry of same vehicle
      vehicleShadeIndexMap[vehicleId]++;

      // Extract date for display
      let date = '';
      if (entry.date) {
        date = entry.date;
      } else if (entry.alertName.includes('(')) {
        date = entry.alertName.split('(')[1]?.replace(')', '') || '';
      }

      outerPieData.push({
        warningName: entry.alertName,
        colorCode: color,
        value: entry.alertValue,
        type: 'outer',
        originalData: entry,
        vehicleId: vehicleId,
        date: date,
        shadeIndex: shadeIndex,
        totalShades: shades.length,
        entryNumber: shadeIndex + 1, // 1-based index for display
      });
    });

    return { innerPieData, outerPieData };
  }, [hasSeparateData, innerRingData, sortedOuterData, vehicleColorMap, vehicleShadeMap]);

  // Prepare data for old single data format
  const prepareSinglePieData = useCallback(() => {
    if (!data || !data.alertNameWithValueList) {
      return { innerPieData: [], outerPieData: [] };
    }

    const alertColorMap: { [key: string]: string } = {};
    data.alertNameWithValueList.forEach((alert: { alertName: string }, index: number) => {
      alertColorMap[alert.alertName] = data.coloursName[index] || 'grey';
    });

    const chartPieValue = data.alertNameWithValueList
      .filter((alert: { alertName: string }) => (selectedAlert ? alert.alertName === selectedAlert : true))
      .map((alert: { alertName: string; alertValue: number }) => ({
        warningName: alert.alertName,
        colorCode: alertColorMap[alert.alertName],
        value: alert.alertValue,
        type: 'single',
      }));

    return {
      innerPieData: chartPieValue,
      outerPieData: chartPieValue,
    };
  }, [data, selectedAlert]);

  const { innerPieData, outerPieData } = hasSeparateData ? prepareSeparatePieData() : prepareSinglePieData();

  // Calculate total value
  const totalAlertValue = hasSeparateData
    ? innerPieData.reduce((sum: number, item: { value: number }) => sum + item.value, 0)
    : innerPieData.reduce((sum: number, item: { value: number }) => sum + item.value, 0);

  const theme = useTheme();

  // Create color mapping for legend
  const createColorMap = useCallback(() => {
    const colorMap: { [key: string]: string } = {};

    if (hasSeparateData) {
      // For separate data, use assigned colors
      [...innerPieData, ...outerPieData].forEach((item) => {
        colorMap[item.warningName] = item.colorCode;
      });
    } else {
      // For single data format
      innerPieData.forEach((item: { warningName: string | number; colorCode: string }) => {
        colorMap[item.warningName] = item.colorCode;
      });
    }

    return colorMap;
  }, [hasSeparateData, innerPieData, outerPieData]);

  const alertColorMapping = createColorMap();

  const handleLegendViewChange = (view: 'inner' | 'outer' | 'both') => {
    setLegendView(view);
  };

  const getLegendData = useCallback(() => {
    if (!hasSeparateData) {
      return innerPieData;
    }

    switch (legendView) {
      case 'inner':
        // Vehicle totals only
        return innerPieData.map((item: { warningName: any }) => ({
          ...item,
          displayName: item.warningName,
          isCombined: true,
        }));

      case 'outer':
        // Group outer ring data by vehicle and show combined
        const vehicleGroups: { [key: string]: any } = {};

        outerPieData.forEach(
          (entry: {
            vehicleId: any;
            warningName: string;
            colorCode: any;
            value: any;
            date: any;
            alertName: string;
          }) => {
            const vehicleId = entry.vehicleId || entry.warningName.split('(')[0].trim();

            if (!vehicleGroups[vehicleId]) {
              vehicleGroups[vehicleId] = {
                warningName: vehicleId,
                colorCode: entry.colorCode,
                value: entry.value,
                type: 'vehicle-combined',
                vehicleId: vehicleId,
                totalEntries: 1,
                dates: [entry.date || entry.alertName.split('(')[1]?.replace(')', '')],
              };
            } else {
              vehicleGroups[vehicleId].value += entry.value;
              vehicleGroups[vehicleId].totalEntries++;
              if (entry.date) {
                vehicleGroups[vehicleId].dates.push(entry.date);
              }
            }
          },
        );

        return Object.values(vehicleGroups);

      case 'both':
      default:
        // Show combined vehicle data with indicator
        const combinedData = innerPieData.map((item: { warningName: any; vehicleId: any }) => ({
          ...item,
          displayName: item.warningName,
          isCombined: true,
          dayCount: outerPieData.filter((e: { vehicleId: any }) => e.vehicleId === item.vehicleId).length,
        }));
        return combinedData;
    }
  }, [hasSeparateData, legendView, innerPieData, outerPieData]);

  const handelTable = () => {
    setIsTableOpen(!isTableOpen);
    if (onTableIconClick) {
      onTableIconClick(selectedAlert);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    if (onCardDataUpdate && chartId) {
      onCardDataUpdate(chartId, data.startDate, data.endDate);
      const startDateFormatted = new Date(data.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(data.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    if (onCardDataUpdate && chartId) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };

  const renderLegendItem = useCallback(
    (entry: any) => {
      const circleSize = 20;
      const borderRadius = circleSize / 2;

      // ALWAYS use the percentage from data if available (percentage_overall from API)
      // Never calculate it ourselves because API has different calculation
      let percentage;

      if (entry.percentage !== undefined && entry.percentage !== null) {
        // Use the actual percentage_overall value from API
        percentage = typeof entry.percentage === 'number' ? entry.percentage.toFixed(2) : entry.percentage;
      } else if (entry.originalData && entry.originalData.percentage_overall !== undefined) {
        // Try to get from originalData
        percentage =
          typeof entry.originalData.percentage_overall === 'number'
            ? entry.originalData.percentage_overall.toFixed(2)
            : entry.originalData.percentage_overall;
      } else {
        // Fallback: calculate only if no percentage data available
        percentage = totalAlertValue > 0 ? ((entry.value / totalAlertValue) * 100).toFixed(2) : '0.00';
      }

      // For outer ring combined entries, show day count
      const dayCountIndicator =
        hasSeparateData && entry.type === 'vehicle-combined' && entry.totalEntries
          ? ` (${entry.totalEntries} days)`
          : '';

      // For both view, show day count
      const combinedIndicator =
        hasSeparateData && entry.isCombined && entry.dayCount ? ` (${entry.dayCount} days)` : '';

      return (
        <Box
          key={`${entry.warningName}-${entry.type || 'combined'}`}
          className="flex items-center"
          sx={{ gap: 4 }}
        >
          <Box className="flex items-center gap-2">
            <Box
              sx={{
                width: circleSize,
                height: circleSize,
                backgroundColor: alertColorMapping[entry.warningName] || entry.colorCode || 'grey',
                borderRadius: `${borderRadius}px`,
                opacity: 1,
                transition: 'all 0.3s ease',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              fontWeight={400}
              sx={(theme) => ({ color: theme.palette.text.secondary })}
              noWrap
            >
              {entry.displayName || entry.warningName}
              {dayCountIndicator}
              {combinedIndicator}
            </Typography>
          </Box>
          <Box className="flex items-center gap-2">
            <Typography
              variant="body2"
              fontWeight={600}
              sx={(theme) => ({ color: theme.palette.text.primary })}
            >
              {entry.value >= 1000 ? `${Math.round(entry.value / 1000)}K` : entry.value.toFixed(2)} km
            </Typography>
            <Typography variant="body2" fontWeight={400} sx={{ color: '#767676' }}>
              ({percentage}%)
            </Typography>
          </Box>
        </Box>
      );
    },
    [totalAlertValue, hasSeparateData, alertColorMapping],
  );

  // Function to render custom tooltip for outer ring
  // Function to render custom tooltip for INNER ring (vehicle totals)
  const renderInnerRingTooltip = (props: any) => {
    const { active, payload } = props;

    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Get the actual percentage_overall from the data
      const actualPercentage =
        data.percentage !== undefined && data.percentage !== null
          ? data.percentage
          : data.originalData && data.originalData.percentage_overall !== undefined
          ? data.originalData.percentage_overall
          : null;

      // Use actual percentage if available, otherwise calculate
      const percentage =
        actualPercentage !== null
          ? typeof actualPercentage === 'number'
            ? actualPercentage.toFixed(2)
            : actualPercentage
          : totalAlertValue > 0
          ? ((data.value / totalAlertValue) * 100).toFixed(2)
          : '0.00';

      // Extract vehicle ID
      let vehicleId = data.vehicleId || data.warningName;

      return (
        <Box
          sx={{
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: `1px solid ${theme.palette.divider}`,
            maxWidth: '300px',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {vehicleId}
          </Typography>
          <Typography variant="body2">
            Total Distance: {data.value >= 1000 ? `${Math.round(data.value / 1000)}K` : data.value.toFixed(2)}{' '}
            km
          </Typography>
          <Typography variant="body2">
            Percentage: {percentage}%
            {actualPercentage !== null && (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: '#666', ml: 1, fontStyle: 'italic' }}
              >
                (from API)
              </Typography>
            )}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Function to render custom tooltip for OUTER ring (daily entries)
  const renderOuterRingTooltip = (props: any) => {
    const { active, payload } = props;

    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Get the actual percentage_day_wise from the data
      const actualPercentage =
        data.originalData && data.originalData.percentage !== undefined ? data.originalData.percentage : null;

      // Use actual percentage if available, otherwise calculate
      const percentage =
        actualPercentage !== null
          ? typeof actualPercentage === 'number'
            ? actualPercentage.toFixed(2)
            : actualPercentage
          : totalAlertValue > 0
          ? ((data.value / totalAlertValue) * 100).toFixed(2)
          : '0.00';

      // Extract vehicle ID and date
      let vehicleId = data.vehicleId;
      let date = data.date;

      if (!vehicleId && data.warningName.includes('(')) {
        const parts = data.warningName.split('(');
        vehicleId = parts[0].trim();
        date = parts[1]?.replace(')', '').trim();
      }

      return (
        <Box
          sx={{
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: `1px solid ${theme.palette.divider}`,
            maxWidth: '300px',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {vehicleId || data.warningName}
          </Typography>
          {date && (
            <Typography variant="body2" fontSize="11px" color="#666">
              Date: {date}
            </Typography>
          )}
          <Typography variant="body2">
            Distance: {data.value >= 1000 ? `${Math.round(data.value / 1000)}K` : data.value.toFixed(2)} km
          </Typography>
          <Typography variant="body2">
            Percentage: {percentage}%
            {actualPercentage !== null && (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: '#666', ml: 1, fontStyle: 'italic' }}
              ></Typography>
            )}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const ringThickness = 20;
  const gapBetweenRings = 2;

  // Radius values
  const outerRingInnerRadius = 78;
  const outerRingOuterRadius = 98;
  const innerRingInnerRadius = 55;
  const innerRingOuterRadius = 75;

  // Expanded modal radius values
  const expandedOuterRingInnerRadius = 78;
  const expandedOuterRingOuterRadius = 98;
  const expandedInnerRingInnerRadius = 55;
  const expandedInnerRingOuterRadius = 75;

  // Calculate current page
  const currentPage = useMemo(() => {
    if (currentPageState !== null) {
      return currentPageState;
    }

    if (chartPaginationInfo?.currentPage !== undefined && chartPaginationInfo?.currentPage !== null) {
      return chartPaginationInfo.currentPage;
    }

    if (currentCursorIndex >= 0) {
      return currentCursorIndex + 2;
    }

    return 1;
  }, [currentPageState, chartPaginationInfo?.currentPage, currentCursorIndex]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (chartPaginationInfo?.totalRecords && chartPaginationInfo?.pageSize) {
      return Math.ceil(chartPaginationInfo.totalRecords / chartPaginationInfo.pageSize);
    }
    return 1;
  }, [chartPaginationInfo?.totalRecords, chartPaginationInfo?.pageSize]);

  // Handle next page
  const handleNextPage = () => {
    const nextCursorAvail = paginatedData?.next_cursor ?? chartPaginationInfo?.nextCursor;
    console.log('🔼 handleNextPage called:', {
      nextCursorAvail,
      currentCursorIndex,
      cursorHistoryLength: cursorHistory.length,
    });

    if (nextCursorAvail && onChartPaginationChange) {
      console.log('✅ Calling onChartPaginationChange with next cursor:', nextCursorAvail);

      setSentNextCursors((prev) => {
        const newSent = [...prev, nextCursorAvail];
        console.log('🔺 sentNextCursors updated:', newSent);
        return newSent;
      });

      // Add to cursor history
      const newIndex = currentCursorIndex + 1;
      if (cursorHistory.length <= newIndex) {
        const newHistory = [...cursorHistory, nextCursorAvail];
        setCursorHistory(newHistory);
        setCurrentCursorIndex(newIndex);
      } else {
        setCurrentCursorIndex(newIndex);
      }

      setCurrentPageState((prev) => (prev !== null ? prev + 1 : (chartPaginationInfo?.currentPage ?? 1) + 1));
      onChartPaginationChange(nextCursorAvail);
    }
  };

  // Handle previous page
  const handlePreviousPage = () => {
    const prevCursorAvail = paginatedData?.previous_cursor ?? chartPaginationInfo?.previousCursor;

    console.log('🔽 handlePreviousPage called:', {
      currentCursorIndex,
      cursorHistoryLength: cursorHistory.length,
      prevCursorAvail,
      sentNextCursorsLength: sentNextCursors.length,
    });

    if (!onChartPaginationChange) {
      console.log('❌ Cannot go to previous page - no onChartPaginationChange callback');
      return;
    }

    // If we have a stack of next cursors we sent, pop it
    if (sentNextCursors.length > 0) {
      const prevLen = sentNextCursors.length;
      const newStack = sentNextCursors.slice(0, -1);
      const cursorToSend = newStack.length > 0 ? newStack[newStack.length - 1] : null;

      console.log('✅ Popping sentNextCursors:', {
        stackBefore: prevLen,
        stackAfter: newStack.length,
        cursorToSend,
      });

      setSentNextCursors(newStack);
      setCurrentPageState((prev) =>
        prev !== null ? Math.max(1, prev - 1) : Math.max(1, (chartPaginationInfo?.currentPage ?? 2) - 1),
      );

      if (cursorToSend === null) {
        console.log('➡️ Returning to first page — sending null cursor');
        onChartPaginationChange(null);
      } else {
        console.log('➡️ Returning to previous page — sending cursor:', cursorToSend);
        onChartPaginationChange(cursorToSend);
      }
      return;
    }

    // Fallback: use API provided previous cursor if available
    if (prevCursorAvail || prevCursorAvail === null) {
      console.log('✅ Calling onChartPaginationChange with previous cursor from API:', prevCursorAvail);
      onChartPaginationChange(prevCursorAvail);
      setCurrentPageState(Math.max(1, currentPage - 1 || 1));
      return;
    }

    console.log('❌ Cannot go to previous page - no history and no previous cursor from API');
  };

  // Check if we can go next/previous
  const hasNextCursor = Boolean(paginatedData?.next_cursor ?? chartPaginationInfo?.nextCursor);
  const hasPreviousCursor = Boolean(paginatedData?.previous_cursor ?? chartPaginationInfo?.previousCursor);

  return (
    <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white">
      <Box className="w-full flex items-center">
        <Box className="flex items-center gap-2 w-full">
          <Typography
            variant="h6"
            fontSize={18}
            sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
          >
            {title}
          </Typography>
          {infoIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: 16,
                height: 16,
              }}
            >
              {infoIcon}
            </Box>
          )}
        </Box>
        <Box className={`flex items-center gap-2`}>
          <Box
            className="flex items-center cursor-pointer"
            onClick={handelTable}
            sx={{ pt: '10px', gap: 0.3 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
            </Box>
            <Typography variant="body2" fontSize={14} sx={(theme) => ({ color: theme.palette.primary.main })}>
              Table
            </Typography>
          </Box>
          <Box className="flex items-center gap-1 cursor-pointer" sx={{ pt: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              <IconButton
                sx={{
                  p: 0,
                  width: '24px !important',
                  height: '24px !important',
                  minWidth: '24px !important',
                  minHeight: '24px !important',
                  maxWidth: '24px !important',
                  maxHeight: '24px !important',
                  flexShrink: 0,
                  display: 'flex !important',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                onClick={handleMenuClick}
              >
                <img
                  src={menuIcon}
                  alt="Menu"
                  width="24"
                  height="24"
                  style={{
                    display: 'block',
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    minHeight: '24px',
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Legend View Selector (for separate data only) */}
      {hasSeparateData && showSeparateLegend && (
        <Box className="flex gap-2 mb-2">
          <Typography variant="body2" sx={{ color: '#666' }}>
            Show:
          </Typography>
          <Box className="flex gap-1">
            {(['both', 'inner', 'outer'] as const).map((view) => (
              <Box
                key={view}
                onClick={() => handleLegendViewChange(view)}
                sx={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  backgroundColor: legendView === view ? '#5547DD' : '#f5f5f5',
                  color: legendView === view ? 'white' : '#666',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: legendView === view ? '#5547DD' : '#e0e0e0',
                  },
                }}
              >
                {view === 'both' ? 'Both' : view === 'inner' ? 'Vehicle Totals' : 'Daily Data'}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>

      <Box className="flex sm:flex-row w-full justify-around h-full items-center flex-col gap-4">
        <Box
          className="flex-1 flex justify-center items-center mt-8"
          sx={{
            minWidth: 0,
            minHeight: 0,
            aspectRatio: '1 / 1',
            width: '100%',
            maxWidth: '350px',
            maxHeight: '350px',
            alignSelf: 'flex-start',
          }}
        >
          {outerPieData.length === 0 && innerPieData.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '300px',
                width: '100%',
              }}
            >
              <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                {/* Outer Ring - Daily entries grouped by vehicle */}
                {outerPieData.length > 0 && (
                  <Pie
                    data={outerPieData}
                    dataKey="value"
                    nameKey="warningName"
                    innerRadius={`${outerRingInnerRadius}%`}
                    outerRadius={`${outerRingOuterRadius}%`}
                    labelLine={false}
                    startAngle={90}
                    endAngle={-270}
                    cx="50%"
                    cy="50%"
                  >
                    {outerPieData.map((entry: any, index: any) => (
                      <Cell
                        key={`outer-cell-${index}`}
                        fill={entry.colorCode}
                        stroke="#fff"
                        strokeWidth={0}
                      />
                    ))}
                  </Pie>
                )}

                {/* Inner Ring - Vehicle totals */}
                {innerPieData.length > 0 && (
                  <Pie
                    data={innerPieData}
                    dataKey="value"
                    nameKey="warningName"
                    innerRadius={`${innerRingInnerRadius}%`}
                    outerRadius={`${innerRingOuterRadius}%`}
                    labelLine={false}
                    startAngle={90}
                    endAngle={-270}
                    cx="50%"
                    cy="50%"
                    paddingAngle={1}
                  >
                    {innerPieData.map((entry: any, index: any) => (
                      <Cell
                        key={`inner-cell-${index}`}
                        fill={entry.colorCode}
                        stroke="#fff"
                        strokeWidth={0}
                      />
                    ))}

                    {/* Center Label */}
                    <Label
                      content={({ viewBox }) => {
                        const cx = (viewBox as any)?.cx ?? 150;
                        const cy = (viewBox as any)?.cy ?? 150;
                        const viewBoxWidth = (viewBox as any)?.width ?? 300;
                        const valueFontSize = Math.max(24, viewBoxWidth * 0.12);
                        const labelFontSize = Math.max(14, viewBoxWidth * 0.07);

                        // Use totalValue prop if provided, otherwise calculate
                        const displayTotal =
                          totalValue ||
                          (totalAlertValue >= 1000
                            ? `${Math.round(totalAlertValue / 1000)}K`
                            : totalAlertValue.toFixed(1));

                        const labelLines =
                          typeof centerLabel === 'string'
                            ? centerLabel.split('\n')
                            : Array.isArray(centerLabel)
                            ? centerLabel
                            : [centerLabel || ''];

                        return (
                          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                            <tspan x={cx} dy="-1.0em" fontSize={valueFontSize} fontWeight="bold">
                              {displayTotal}
                            </tspan>
                            {labelLines.map((line, index) => (
                              <tspan
                                key={index}
                                x={cx}
                                dy={index === 0 ? '2.2em' : '1.2em'}
                                fontSize={labelFontSize}
                                fontWeight={400}
                                fill="rgba(0, 0, 0, 0.6)"
                              >
                                {line}
                              </tspan>
                            ))}
                          </text>
                        );
                      }}
                    />
                  </Pie>
                )}

                <Tooltip content={renderOuterRingTooltip} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Legend Section */}
        {outerPieData.length > 0 || innerPieData.length > 0 ? (
          <Box className="flex flex-col justify-center">
            <Box className="flex flex-col gap-3 w-full" style={{ maxHeight: '288px', overflowY: 'auto' }}>
              {getLegendData().map((entry: any) => renderLegendItem(entry))}
            </Box>
          </Box>
        ) : null}
      </Box>

      {/* Chart Pagination Controls - ADDED */}
      {paginatedData && onChartPaginationChange && (
        <Box display="flex" justifyContent="center" alignItems="center" width={'100%'} gap={1} mt={2}>
          {/* Previous Button */}
          <IconButton
            size="small"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 && !hasPreviousCursor}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              '&:disabled': {
                opacity: 0.5,
                borderColor: '#ccc',
                cursor: 'not-allowed',
              },
            }}
          >
            <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
          </IconButton>

          {/* Page Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Current page - active */}
            <Box
              sx={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                backgroundColor: theme.palette.primary.main,
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              {currentPage}
            </Box>

            {/* Next page (light) - show if there is a next page available */}
            {(hasNextCursor || currentPage < totalPages) && (
              <Box
                // onClick={() => handlePageClick(currentPage + 1)}
                sx={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  color: '#808080',
                  fontSize: '12px',
                  fontWeight: 400,
                  '&:hover': {
                    backgroundColor: '#F5F5F5',
                  },
                }}
              >
                {currentPage + 1}
              </Box>
            )}

            {/* If there are more pages beyond next, show ellipsis + last page */}
            {totalPages > currentPage + 1 && (
              <>
                <Typography sx={{ color: '#808080', fontSize: '12px' }}>...</Typography>
                <Box
                  // onClick={() => handlePageClick(totalPages)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: totalPages === currentPage ? '#FFFFFF' : '#808080',
                    backgroundColor: totalPages === currentPage ? '#5547DD' : 'transparent',
                    fontSize: '12px',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: totalPages === currentPage ? '#5547DD' : '#F5F5F5',
                    },
                  }}
                >
                  {totalPages}
                </Box>
              </>
            )}
          </Box>

          {/* Next Button */}
          <IconButton
            size="small"
            onClick={handleNextPage}
            disabled={!hasNextCursor}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              '&:disabled': {
                opacity: 0.5,
                borderColor: '#ccc',
                cursor: 'not-allowed',
              },
            }}
          >
            <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
          </IconButton>
        </Box>
      )}

      {/* Table Modal */}
      <Modal
        open={isTableOpen}
        onClose={() => setIsTableOpen(false)}
        aria-labelledby="alert-table-modal"
        aria-describedby="alert-table-description"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'auto',
            height: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <CommonAnalysisTable
            title={tableTitle}
            headerText={tableHeaderText}
            columns={tableColumns}
            data={tableData}
            noDataMessage="No data available for this selection"
            onClose={() => setIsTableOpen(false)}
            paginationInfo={paginationInfo}
            onPaginationChange={onPaginationChange}
          />
        </Box>
      </Modal>

      {/* Expanded Chart Modal */}
      <Modal
        open={isExpandedModalOpen}
        onClose={handleCloseExpandedModal}
        aria-labelledby="expanded-pie-chart-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
            maxWidth: { xs: 400, sm: 600, md: 900, lg: 1000 },
            maxHeight: { xs: '85vh', sm: '90vh', md: '95vh' },
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: 24,
            p: { xs: 2, sm: 2.5, md: 3 },
            outline: 'none',
            overflow: 'auto',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: { xs: '18px', sm: '24px', md: '28px' },
                  lineHeight: { xs: '27px', sm: '36px', md: '42px' },
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Edit Chart Button */}
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  color: '#5547DD',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>

              {/* Collapse Button */}
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#5547DD',
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Expanded Double Pie Chart */}
          <Box className="flex sm:flex-row w-full justify-around h-full items-center flex-col gap-4">
            <Box
              className="flex-1 flex justify-center items-center"
              sx={{
                minWidth: 0,
                minHeight: 0,
                aspectRatio: '1 / 1',
                width: '100%',
                maxWidth: '400px',
                maxHeight: '400px',
              }}
            >
              {outerPieData.length === 0 && innerPieData.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    width: '100%',
                  }}
                >
                  <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    {/* Outer Ring - Daily entries grouped */}
                    {outerPieData.length > 0 && (
                      <Pie
                        data={outerPieData}
                        dataKey="value"
                        nameKey="warningName"
                        innerRadius={`${expandedOuterRingInnerRadius}%`}
                        outerRadius={`${expandedOuterRingOuterRadius}%`}
                        labelLine={false}
                        startAngle={90}
                        endAngle={-270}
                        cx="50%"
                        cy="50%"
                      >
                        {outerPieData.map((entry: any, index: any) => (
                          <Cell
                            key={`outer-cell-${index}`}
                            fill={entry.colorCode}
                            stroke="#fff"
                            strokeWidth={0}
                          />
                        ))}
                      </Pie>
                    )}

                    {/* Inner Ring - Vehicle totals */}
                    {innerPieData.length > 0 && (
                      <Pie
                        data={innerPieData}
                        dataKey="value"
                        nameKey="warningName"
                        innerRadius={`${expandedInnerRingInnerRadius}%`}
                        outerRadius={`${expandedInnerRingOuterRadius}%`}
                        labelLine={false}
                        startAngle={90}
                        endAngle={-270}
                        cx="50%"
                        cy="50%"
                        paddingAngle={1}
                      >
                        {innerPieData.map((entry: any, index: any) => (
                          <Cell
                            key={`inner-cell-${index}`}
                            fill={entry.colorCode}
                            stroke="#fff"
                            strokeWidth={0}
                          />
                        ))}

                        <Label
                          content={({ viewBox }) => {
                            const cx = (viewBox as any)?.cx ?? 150;
                            const cy = (viewBox as any)?.cy ?? 150;
                            const viewBoxWidth = (viewBox as any)?.width ?? 300;
                            const valueFontSize = Math.max(28, viewBoxWidth * 0.12);
                            const labelFontSize = Math.max(12, viewBoxWidth * 0.06);

                            const displayTotal =
                              totalValue ||
                              (totalAlertValue >= 1000
                                ? `${Math.round(totalAlertValue / 1000)}K`
                                : totalAlertValue.toFixed(1));

                            const labelLines =
                              typeof centerLabel === 'string'
                                ? centerLabel.split('\n')
                                : Array.isArray(centerLabel)
                                ? centerLabel
                                : [centerLabel || ''];

                            return (
                              <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                <tspan x={cx} dy="-1.0em" fontSize={valueFontSize} fontWeight="bold">
                                  {displayTotal}
                                </tspan>
                                {labelLines.map((line, index) => (
                                  <tspan
                                    key={index}
                                    x={cx}
                                    dy={index === 0 ? '2.2em' : '1.2em'}
                                    fontSize={labelFontSize}
                                    fontWeight={400}
                                    fill="rgba(0, 0, 0, 0.6)"
                                  >
                                    {line}
                                  </tspan>
                                ))}
                              </text>
                            );
                          }}
                        />
                      </Pie>
                    )}

                    <Tooltip content={renderOuterRingTooltip} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>

            {/* Legend Section */}
            {outerPieData.length > 0 || innerPieData.length > 0 ? (
              <Box className="flex flex-col justify-center">
                <Box className="flex flex-col gap-3 w-full" style={{ maxHeight: '288px', overflowY: 'auto' }}>
                  {getLegendData().map((entry: any) => renderLegendItem(entry))}
                </Box>
              </Box>
            ) : null}
          </Box>

          {/* Expanded Modal Pagination Controls - ADDED */}
          {paginatedData && onChartPaginationChange && (
            <Box display="flex" justifyContent="center" alignItems="center" width={'100%'} gap={1} mt={2}>
              {/* Previous Button */}
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 && !hasPreviousCursor}
                sx={{
                  border: '1px solid',
                  borderColor: theme.palette.primary.main,
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  '&:disabled': {
                    opacity: 0.5,
                    borderColor: '#ccc',
                    cursor: 'not-allowed',
                  },
                }}
              >
                <img src={ArrowLeftIcon} alt="Previous" style={{ width: '16px', height: '16px' }} />
              </IconButton>

              {/* Page Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1B1B1B',
                    fontSize: '12px',
                    fontWeight: 400,
                    minWidth: '120px',
                    textAlign: 'center',
                  }}
                >
                  {(() => {
                    const currentOffset = paginatedData?.next_cursor?.offset || 0;
                    const displayedCount = paginatedData?.data?.length || 0;
                    const totalRecords = chartPaginationInfo?.totalRecords || 0;

                    if (totalRecords > 0) {
                      const start = currentOffset - displayedCount + 1;
                      const end = currentOffset;
                      return `Vehicles ${Math.max(1, start)}-${end} of ${totalRecords}`;
                    } else {
                      const start = currentOffset - displayedCount + 1;
                      const end = currentOffset;
                      return `Showing vehicles ${Math.max(1, start)}-${end}`;
                    }
                  })()}
                </Typography>
              </Box>

              {/* Next Button */}
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={!hasNextCursor}
                sx={{
                  border: '1px solid',
                  borderColor: theme.palette.primary.main,
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  '&:disabled': {
                    opacity: 0.5,
                    borderColor: '#ccc',
                    cursor: 'not-allowed',
                  },
                }}
              >
                <img src={ArrowRightIcon} alt="Next" style={{ width: '16px', height: '16px' }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Edit Data Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        onReset={() => console.log('Reset clicked')}
        cardTitle={baseTitle}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />
    </Box>
  );
};

export default CommonDoublePieChart;
